import React, { Component } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { clearCompanionSession } from "../../auth/session";
import {
  getDashboardBillingPath,
  getDashboardCalendarPath,
  getDashboardLoginPath,
  getDashboardOverviewPath,
  getDashboardUsagePath,
} from "../../utils/dashboardPaths";
import { isMobileUserAgent } from "../../utils/isMobileUserAgent";
import "./AppHeader.scss";
import logoColor from "../../assets/logo-color.png";
import EmailAjentIcon from "../../assets/emailajent-icon.png";
import hamburgerIcon from "../../assets/hamburger-icon.png";

interface AppHeaderProps {
  variant: "marketing" | "dashboard";
}

interface AppHeaderState {
  menuOpen: boolean;
}

function AppHeaderDashboardLogout(props: { onBeforeNavigate: () => void }): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="app-header-link app-header-link--mobile app-header-link--button"
      onClick={() => {
        props.onBeforeNavigate();
        clearCompanionSession();
        navigate(getDashboardLoginPath(), { replace: true });
      }}
    >
      Logout
    </button>
  );
}

class AppHeader extends Component<AppHeaderProps, AppHeaderState> {
  constructor(props: AppHeaderProps) {
    super(props);
    this.state = { menuOpen: false };
  }

  toggleMenu(): void {
    this.setState((prev) => ({ menuOpen: !prev.menuOpen }));
  }

  closeMenu(): void {
    this.setState({ menuOpen: false });
  }

  private rootClass(): string {
    const { variant } = this.props;
    return variant === "dashboard" ? "app-header app-header--dashboard" : "app-header";
  }

  private renderBrand(): React.JSX.Element {
    return (
      <NavLink to="/" className="app-header-brand" end>
        <img className="app-header-brand-icon" src={EmailAjentIcon} alt="Email Ajent icon" />
        <img className="app-header-brand-logo" src={logoColor} alt="Email Ajent logo" />
      </NavLink>
    );
  }

  private renderMarketingNavItems(options: { closeOnNavigate: boolean }): React.ReactNode {
    const onNav = options.closeOnNavigate ? () => this.closeMenu() : undefined;
    return (
      <>
        {/* <a href="#features" className="app-header-link" onClick={onNav}>
          Features
        </a>
        <NavLink to="/pricing" className="app-header-link" onClick={onNav}>
          Pricing
        </NavLink>
        <a href="#contact-us" className="app-header-link" onClick={onNav}>
          Contact Us
        </a>
        <a href="#faq" className="app-header-link" onClick={onNav}>
          FAQ
        </a> */}
        <NavLink
          to={getDashboardOverviewPath()}
          className="app-header-link app-header-link--signin"
          onClick={onNav}
        >
          Sign in
        </NavLink>
        <Link to={getDashboardLoginPath()} className="app-header-signup" onClick={onNav}>
          Sign Up
        </Link>
      </>
    );
  }

  private renderDashboardNavMobile(): React.ReactNode {
    const onNav = (): void => this.closeMenu();
    return (
      <>
        <NavLink
          to={getDashboardOverviewPath()}
          end
          className={({ isActive }) =>
            isActive ? "app-header-link app-header-link--mobile active" : "app-header-link app-header-link--mobile"
          }
          onClick={onNav}
        >
          Overview
        </NavLink>
        <NavLink
          to={getDashboardCalendarPath()}
          className={({ isActive }) =>
            isActive ? "app-header-link app-header-link--mobile active" : "app-header-link app-header-link--mobile"
          }
          onClick={onNav}
        >
          Link calendar
        </NavLink>
        <NavLink
          to={getDashboardUsagePath()}
          className={({ isActive }) =>
            isActive ? "app-header-link app-header-link--mobile active" : "app-header-link app-header-link--mobile"
          }
          onClick={onNav}
        >
          Usage
        </NavLink>
        {/* <NavLink
          to={getDashboardBillingPath()}
          className={({ isActive }) =>
            isActive ? "app-header-link app-header-link--mobile active" : "app-header-link app-header-link--mobile"
          }
          onClick={onNav}
        >
          Billing
        </NavLink> */}
        <AppHeaderDashboardLogout onBeforeNavigate={onNav} />
      </>
    );
  }

  private renderDesktop(): React.JSX.Element {
    const { variant } = this.props;
    return (
      <header className={this.rootClass()}>
        <div className="app-header-inner">
          {this.renderBrand()}
          <nav className="app-header-nav" aria-label="Primary">
            {variant === "marketing" ? this.renderMarketingNavItems({ closeOnNavigate: false }) : null}
          </nav>
        </div>
      </header>
    );
  }

  private renderMobile(): React.JSX.Element {
    const { variant } = this.props;
    const { menuOpen } = this.state;
    const panelId =
      variant === "marketing"
        ? "app-header-mobile-panel-marketing"
        : "app-header-mobile-panel-dashboard";

    return (
      <header className={this.rootClass()}>
        <div className="app-header-inner app-header-inner--mobile">
          <div className="app-header-mobile-top">
            <button
              type="button"
              className="app-header-menu-toggle"
              aria-expanded={menuOpen}
              aria-controls={panelId}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => this.toggleMenu()}
            >
              <img
                className="app-header-menu-toggle-icon"
                src={hamburgerIcon}
                alt=""
                width={24}
                height={24}
                decoding="async"
                aria-hidden="true"
              />
            </button>
            <div className="app-header-mobile-brand-wrap">{this.renderBrand()}</div>
            <div className="app-header-mobile-top-spacer" aria-hidden="true" />
          </div>
          <nav
            id={panelId}
            className="app-header-mobile-nav"
            aria-label={variant === "marketing" ? "Primary" : "Dashboard sections"}
            hidden={!menuOpen}
          >
            {variant === "marketing" ? (
              <div className="app-header-mobile-nav-inner">{this.renderMarketingNavItems({ closeOnNavigate: true })}</div>
            ) : (
              <div className="app-header-mobile-nav-inner">{this.renderDashboardNavMobile()}</div>
            )}
          </nav>
        </div>
      </header>
    );
  }

  render(): React.JSX.Element {
    return isMobileUserAgent() ? this.renderMobile() : this.renderDesktop();
  }
}

export default AppHeader;
